import React, { useState, useEffect, useCallback } from 'react';
import { LifeBuoy, RefreshCw, Check, Send, AlertTriangle, Bot, Loader2, ChevronDown, ChevronUp, MailCheck, MailX, Undo2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  escalated: { label: 'Needs You', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  new: { label: 'New', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  processing: { label: 'AI working…', cls: 'bg-gray-100 text-gray-600 border-gray-200' },
  answered: { label: 'Answered', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  resolved: { label: 'Resolved', cls: 'bg-gray-100 text-gray-500 border-gray-200' },
};

const QueryCard = ({ q, onRefresh }) => {
  const [reply, setReply] = useState('');
  const [showReply, setShowReply] = useState(false);
  const [showAi, setShowAi] = useState(false);
  const [busy, setBusy] = useState(false);
  const meta = STATUS_META[q.status] || STATUS_META.new;
  const replySent = Boolean(q.answered_by || q.admin_reply);

  const act = async (action, body) => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/admin/queries/${q.id}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success(action === 'reply' ? 'Reply emailed' : action === 'reopen' ? 'Query reopened — it\'s back in "Needs You"' : 'Marked resolved');
      setShowReply(false);
      onRefresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`bg-white rounded-xl border p-4 ${q.status === 'escalated' ? 'border-amber-300 ring-1 ring-amber-200' : ''}`} data-testid={`query-card-${q.id}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${meta.cls}`}>{meta.label}</span>
          {q.category && <span className="text-[11px] text-gray-400 ml-2 uppercase tracking-wide">{q.category}</span>}
        </div>
        <span className="text-xs text-gray-400">{new Date(q.created_at).toLocaleString()}</span>
      </div>
      <p className="text-sm text-gray-900 mt-2">{q.message}</p>
      <p className="text-xs text-gray-500 mt-1">
        {q.name || 'Anonymous'}{q.email ? ` · ${q.email}` : ' · no email'}{q.page ? ` · from ${q.page}` : ''}
      </p>
      <p className={`text-xs mt-2 flex items-center gap-1.5 font-medium ${replySent ? 'text-emerald-700' : 'text-red-600'}`} data-testid={`query-reply-status-${q.id}`}>
        {replySent
          ? <><MailCheck className="w-3.5 h-3.5" /> {q.answered_by === 'ai' ? 'AI replied by email' : 'You replied by email'}</>
          : <><MailX className="w-3.5 h-3.5" /> No reply sent to this customer</>}
      </p>
      {q.status === 'escalated' && q.escalation_reason && (
        <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mt-2 flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {q.escalation_reason}
        </p>
      )}
      {q.ai_reply_html && (
        <div className="mt-2">
          <button onClick={() => setShowAi(!showAi)} className="text-xs text-minted-600 flex items-center gap-1 font-medium" data-testid={`query-ai-toggle-${q.id}`}>
            <Bot className="w-3.5 h-3.5" /> {q.answered_by === 'ai' ? 'AI reply sent to user' : 'AI drafted answer'} {showAi ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {showAi && <div className="text-xs text-gray-600 bg-gray-50 rounded-lg p-3 mt-1 [&_p]:mb-1" dangerouslySetInnerHTML={{ __html: q.ai_reply_html }} />}
        </div>
      )}
      {q.admin_reply && <p className="text-xs text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2 mt-2">Your reply: {q.admin_reply}</p>}
      {q.status !== 'resolved' && (
        <div className="flex gap-2 mt-3 flex-wrap">
          {q.email && (
            <Button size="sm" variant="outline" onClick={() => setShowReply(!showReply)} data-testid={`query-reply-toggle-${q.id}`}>
              <Send className="w-3 h-3 mr-1" /> Reply
            </Button>
          )}
          <Button size="sm" variant="ghost" disabled={busy} data-testid={`query-resolve-${q.id}`}
            onClick={() => {
              if (!replySent && !window.confirm('No reply has been sent to this customer yet. Resolve anyway?')) return;
              act('resolve');
            }}>
            <Check className="w-3 h-3 mr-1" /> Resolve
          </Button>
        </div>
      )}
      {q.status === 'resolved' && (
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          {!replySent && (
            <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200">Resolved without reply</span>
          )}
          <Button size="sm" variant="outline" onClick={() => act('reopen')} disabled={busy} data-testid={`query-reopen-${q.id}`}>
            <Undo2 className="w-3 h-3 mr-1" /> Undo resolve
          </Button>
        </div>
      )}
      {showReply && (
        <div className="mt-2 space-y-2">
          <Textarea rows={3} placeholder="Write your reply — it's emailed with Minted branding" value={reply} onChange={e => setReply(e.target.value)} data-testid={`query-reply-input-${q.id}`} />
          <Button size="sm" onClick={() => act('reply', { message: reply })} disabled={busy || !reply.trim()} className="bg-minted-600 hover:bg-minted-700" data-testid={`query-reply-send-${q.id}`}>
            {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />} Send reply
          </Button>
        </div>
      )}
    </div>
  );
};

export const QueriesTab = () => {
  const [queries, setQueries] = useState([]);
  const [summary, setSummary] = useState({});
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [qRes, sRes] = await Promise.all([
        fetch(`${API}/api/admin/queries${filter !== 'all' ? `?status=${filter}` : ''}`),
        fetch(`${API}/api/admin/queries/summary`),
      ]);
      setQueries(await qRes.json());
      setSummary(await sRes.json());
    } catch {
      toast.error('Failed to load queries');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const filters = [
    ['all', `All`],
    ['escalated', `Needs You (${summary.escalated || 0})`],
    ['new', 'New'],
    ['answered', 'Answered'],
    ['resolved', 'Resolved'],
  ];

  return (
    <div className="space-y-4" data-testid="queries-tab">
      <div className="rounded-2xl p-5 text-white" style={{ background: 'linear-gradient(135deg, #12281c 0%, #1a3a2a 100%)' }}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(201,169,98,0.15)', border: '1px solid rgba(201,169,98,0.4)' }}>
              <LifeBuoy className="w-5 h-5" style={{ color: '#c9a962' }} />
            </div>
            <div>
              <h3 className="font-serif text-lg" style={{ fontFamily: 'Georgia, serif' }}>Queries Inbox</h3>
              <p className="text-xs text-white/60">Your AI Support agent answers customers automatically — it only pings you when it's stuck.</p>
            </div>
          </div>
          <div className="flex gap-4 text-center">
            <div><p className="text-xl font-bold" style={{ color: '#c9a962' }}>{summary.escalated || 0}</p><p className="text-[10px] text-white/60 uppercase tracking-wide">Need you</p></div>
            <div><p className="text-xl font-bold">{summary.answered || 0}</p><p className="text-[10px] text-white/60 uppercase tracking-wide">AI answered</p></div>
            <div><p className="text-xl font-bold">{summary.resolved || 0}</p><p className="text-[10px] text-white/60 uppercase tracking-wide">Resolved</p></div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-1 flex-wrap">
          {filters.map(([id, label]) => (
            <button key={id} onClick={() => setFilter(id)} data-testid={`queries-filter-${id}`}
              className={`text-xs px-3 py-1.5 rounded-full transition-colors ${filter === id ? 'bg-minted-600 text-white' : 'bg-white text-gray-600 border hover:bg-gray-50'}`}>
              {label}
            </button>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={load}><RefreshCw className="w-3 h-3 mr-1" /> Refresh</Button>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl p-8 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : queries.length === 0 ? (
        <div className="bg-white rounded-xl border p-8 text-center text-sm text-gray-400" data-testid="queries-empty">
          No queries here — customers can ask via the Help button on any page.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {queries.map(q => <QueryCard key={q.id} q={q} onRefresh={load} />)}
        </div>
      )}
    </div>
  );
};

export default QueriesTab;
